<script lang="ts">
    import type { Curator } from '$lib/collectui/types';
    import { CURATOR_SELECT } from '$lib/collectui/selects';
    import { buttonVariants } from '$lib/components/ui/button';
    import Button from '$lib/components/ui/button/button.svelte';
    import * as Dialog from '$lib/components/ui/dialog';
    import Input from '$lib/components/ui/input/input.svelte';
    import Label from '$lib/components/ui/label/label.svelte';
    import { supabase } from '$lib/supabase';
    import { cn } from '$lib/utils';
    import { Check, Loader2, Plus, X } from 'lucide-svelte';
    import { toast } from 'svelte-sonner';
    import CuratorCard from './CuratorCard.svelte';

    const {
        onadd,
    }: {
        onadd: (curator: Curator) => void;
    } = $props();

    type Profile = {
        id: string;
        name: string;
        email: string;
    };

    let open = $state(false);
    let step = $state(0);
    let users: Profile[] = $state([]);
    let search: string = $state('');
    let username: string = $state('');
    let selectedUser: Profile | undefined = $state();
    let checking = $state(false);
    let checkResult = $state('');
    let isValid = $derived(checkResult === 'Username valid');
    let profile: Curator | undefined = $state();
    let adding = $state(false);

    let timer: NodeJS.Timeout;
    async function handleSearch() {
        if (!search.trim()) {
            users = [];
            return;
        }
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(async () => {
            const { data, error } = await supabase
                .from('profiles')
                .select()
                .or(`email.ilike.%${search}%,name.ilike.%${search}%`);
            if (error) {
                throw error;
            }

            users = data;
        }, 200);
    }

    async function handleCheck() {
        if (!username.trim()) return;

        checking = true;
        try {
            const { data, error } = await supabase.functions.invoke(
                'curator-profile',
                {
                    method: 'POST',
                    body: {
                        username,
                    },
                },
            );
            if (error) {
                checkResult = 'Invalid username';
                throw error;
            } else if (data) {
                step = 2;
                profile = data;
            }
        } finally {
            checking = false;
        }
    }

    async function handleAdd() {
        if (!profile || !selectedUser) return;

        adding = true;
        try {
            // curator-profile has already saved the profile to x_profiles.
            const { data: xProfile } = await supabase
                .from('x_profiles')
                .select('id')
                .ilike('username', profile.username)
                .maybeSingle();

            const { data, error } = await supabase
                .from('collectui_curators')
                .insert({
                    id: selectedUser.id,
                    user_id: selectedUser.id,
                    x_profile_id: xProfile?.id ?? null,
                    rest_id: profile.rest_id ?? null,
                })
                .select(CURATOR_SELECT)
                .single<Curator>();

            if (error) {
                console.error('Database error:', error);
                toast.error(error.message);
            } else {
                console.log('Successfully inserted curator data');
                toast.success('Successfully added curator');
                onadd(data);
                open = false;
                reset();
            }
        } finally {
            adding = false;
        }
    }

    function reset() {
        step = 0;
        selectedUser = undefined;
        username = '';
        search = '';
        users = [];
    }
</script>

<Dialog.Root
    bind:open
    onOpenChange={(open) => {
        if (!open) reset();
    }}>
    <Dialog.Trigger class={cn(buttonVariants(), 'flex gap-2')}>
        <Plus />
        Add Curator
    </Dialog.Trigger>
    <Dialog.Content>
        <Dialog.Header>
            <Dialog.Title>Add Curator</Dialog.Title>
        </Dialog.Header>
        {#if step === 0}
            {#if selectedUser}
                <div
                    class="rounded-lg relative border border-white/5 bg-white/5 p-4 flex flex-col gap-1">
                    <span class="font-medium text-gray-100">
                        {selectedUser.email}
                    </span>
                    <span class="text-sm text-gray-400">
                        {selectedUser.name}
                    </span>

                    <button
                        onclick={() => {
                            selectedUser = undefined;
                        }}
                        class="absolute top-2 flex p-2 items-center justify-center right-2 hover:bg-white/5 rounded-full">
                        <X class="size-4" />
                    </button>
                </div>
            {:else}
                <div class="space-y-1">
                    <Label>User Email</Label>
                    <div class="relative">
                        <Input
                            type="search"
                            bind:value={search}
                            placeholder="Enter user email"
                            oninput={handleSearch} />

                        {#if users.length}
                            <div
                                class="absolute z-50 w-full mt-1 bg-popover rounded-lg shadow-lg border border-white/5 max-h-[300px] overflow-y-auto">
                                {#each users as user}
                                    <button
                                        class="w-full px-4 py-2 text-left hover:bg-white/5 transition-colors duration-150 flex flex-col gap-0.5"
                                        onclick={() => {
                                            selectedUser = user;
                                        }}>
                                        <span class="font-medium text-gray-100">
                                            {user.email}
                                        </span>
                                        <span class="text-sm text-gray-400">
                                            {user.name}
                                        </span>
                                    </button>
                                {/each}
                            </div>
                        {/if}
                    </div>
                </div>
            {/if}
        {:else if step === 1}
            <div class="space-y-1">
                <Label>X Username</Label>
                <Input
                    bind:value={username}
                    placeholder="Enter x.com profile username"
                    oninput={() => (checkResult = '')} />
                {#if checkResult && isValid}
                    <div class="text-green-500 flex items-center gap-2">
                        <Check />
                        {checkResult}
                    </div>
                {:else if checkResult}
                    <div class="text-red-500 flex items-center gap-2">
                        <X />
                        {checkResult}
                    </div>
                {/if}
            </div>
        {:else if step === 2 && profile}
            <CuratorCard curator={profile} />
        {/if}
        <Dialog.Footer>
            <Dialog.Close class={cn(buttonVariants({ variant: 'outline' }))}>
                Cancel
            </Dialog.Close>
            {#if selectedUser && step === 0}
                <Button onclick={() => step++}>Continue</Button>
            {:else if step === 1}
                {#if !checkResult}
                    <Button
                        onclick={handleCheck}
                        disabled={!username || checking}
                        class="flex gap-2 items-center">
                        {#if checking}
                            <Loader2 class="animate-spin size-4" />
                        {/if}
                        Check
                    </Button>
                {/if}
            {:else if step === 2}
                <Button variant="outline" onclick={() => (step = 1)}>
                    Back
                </Button>
                <Button onclick={handleAdd} disabled={adding}>Add</Button>
            {/if}
        </Dialog.Footer>
    </Dialog.Content>
</Dialog.Root>
